// `@sl/core/tree` — the expansion state (API §4.5 row 6; REQ-910 AC3).
//
// Which rows are open is a set of entity ids (campaigns and ad sets). It is client-side only, never sent to the
// server, and remembered per user and account: on this device in MVP (a browser-storage adapter, cleared by
// "Reset demo" and "Start fresh" — QA's R-33 default, REQ-910 AC3 v1.3), a user preference that follows the
// user once REQ-003 brings sign-in — the store below takes any adapter of the `storage.ts` shape, so the switch
// is one adapter, not a new model. Expanding a campaign opens its ad sets only; each ad set's ads stay closed
// until asked for (a separate id in the set). First visit: everything collapsed (`EMPTY_EXPANSION`).
//
// The state is immutable — every operation returns a new state — so React can hold it as is. The serialised
// form is a small JSON document `{ "v": 1, "expanded": [...ids sorted] }`; `parseExpansion` never throws: a
// corrupt or foreign value opens the tree collapsed (the demo must always open, the same rule as `DemoStore`).

export interface ExpansionState {
  readonly expanded: ReadonlySet<string>;
  /**
   * BUG-227: rows the owner CLOSED while a filter or a search was holding them open. A held-open row
   * (`autoExpanded`) is open by default, not locked open: the override wins until the owner opens the row
   * again or the filter goes. Optional so that every `{ expanded }` literal stays a valid state.
   */
  readonly collapsed?: ReadonlySet<string>;
}

export const EMPTY_EXPANSION: ExpansionState = Object.freeze({
  expanded: new Set<string>(),
  collapsed: new Set<string>(),
});

/** The ids a state holds closed against a filter (BUG-227) — empty for a state written before the field existed. */
export function heldClosed(state: ExpansionState): ReadonlySet<string> {
  return state.collapsed ?? EMPTY_SET;
}

const EMPTY_SET: ReadonlySet<string> = new Set<string>();

/** "Expand all" is refused above this many resulting rows (REQ-910 AC3). */
export const EXPAND_ALL_LIMIT = 500;

/** Bumped only if the serialised shape changes; an older `v` parses as empty. */
export const EXPANSION_FORMAT_VERSION = 1;

export function isExpanded(state: ExpansionState, id: string): boolean {
  return state.expanded.has(id);
}

export function expansionOf(ids: Iterable<string>): ExpansionState {
  return { expanded: new Set(ids) };
}

export function expand(state: ExpansionState, id: string): ExpansionState {
  const closed = heldClosed(state);
  if (state.expanded.has(id) && !closed.has(id)) return state;
  const next = new Set(state.expanded);
  next.add(id);
  // an explicit open also lifts a BUG-227 override, so the filter may hold the row open again
  const stillClosed = new Set(closed);
  stillClosed.delete(id);
  return { expanded: next, collapsed: stillClosed };
}

export function collapse(state: ExpansionState, id: string): ExpansionState {
  if (!state.expanded.has(id)) return state;
  const next = new Set(state.expanded);
  next.delete(id);
  return { expanded: next, collapsed: heldClosed(state) };
}

/** BUG-227: close a row a filter is holding open — remembered as an override until the row is opened again. */
export function closeHeld(state: ExpansionState, id: string): ExpansionState {
  const expanded = new Set(state.expanded);
  expanded.delete(id);
  const collapsed = new Set(heldClosed(state));
  collapsed.add(id);
  return { expanded, collapsed };
}

/**
 * The next state after the chevron. `heldOpen` is the row's `autoExpanded` (BUG-227): a row the filter holds
 * open closes with an override, and an overridden row opens by lifting it; any other row simply flips.
 */
export function toggle(state: ExpansionState, id: string, heldOpen = false): ExpansionState {
  if (heldOpen) return heldClosed(state).has(id) ? expand(state, id) : closeHeld(state, id);
  return state.expanded.has(id) ? collapse(state, id) : expand(state, id);
}

/**
 * "Collapse all": every remembered open row closed and, since BUG-227, every row the filter is holding open
 * (`heldOpen`: their ids) closed with an override — so the button does what it says under a filter too.
 */
export function collapseAll(heldOpen: Iterable<string> = []): ExpansionState {
  const collapsed = new Set(heldOpen);
  return collapsed.size === 0 ? EMPTY_EXPANSION : { expanded: new Set<string>(), collapsed };
}

/** Drop ids that are not (any longer) parents of `known` — after a refresh, before saving; keeps the record small. */
export function pruneExpansion(state: ExpansionState, known: ReadonlySet<string>): ExpansionState {
  let changed = false;
  const next = new Set<string>();
  for (const id of state.expanded) {
    if (known.has(id)) next.add(id);
    else changed = true;
  }
  const closed = new Set<string>();
  for (const id of heldClosed(state)) {
    if (known.has(id)) closed.add(id);
    else changed = true;
  }
  return changed ? { expanded: next, collapsed: closed } : state;
}

// ---- serialisation ------------------------------------------------------------------------------------------

export function serializeExpansion(state: ExpansionState): string {
  const closed = [...heldClosed(state)].sort();
  // the field is written only when it says something, so a state with no override reads exactly as before
  return JSON.stringify(
    closed.length === 0
      ? { v: EXPANSION_FORMAT_VERSION, expanded: [...state.expanded].sort() }
      : { v: EXPANSION_FORMAT_VERSION, expanded: [...state.expanded].sort(), collapsed: closed },
  );
}

/** Never throws; anything that is not this module's document (or an older version) is the empty state. */
export function parseExpansion(raw: string | null | undefined): ExpansionState {
  if (typeof raw !== 'string' || raw === '') return EMPTY_EXPANSION;
  let doc: unknown;
  try {
    doc = JSON.parse(raw);
  } catch {
    return EMPTY_EXPANSION;
  }
  if (typeof doc !== 'object' || doc === null) return EMPTY_EXPANSION;
  const { v, expanded, collapsed } = doc as {
    v?: unknown;
    expanded?: unknown;
    collapsed?: unknown;
  };
  if (v !== EXPANSION_FORMAT_VERSION || !Array.isArray(expanded)) return EMPTY_EXPANSION;
  const ids: string[] = [];
  for (const id of expanded) if (typeof id === 'string' && id !== '') ids.push(id);
  const closed: string[] = [];
  if (Array.isArray(collapsed))
    for (const id of collapsed) if (typeof id === 'string' && id !== '') closed.push(id);
  return ids.length === 0 && closed.length === 0
    ? EMPTY_EXPANSION
    : { expanded: new Set(ids), collapsed: new Set(closed) };
}

// ---- the store ------------------------------------------------------------------------------------------------

/**
 * The adapter shape — the same three calls as `DemoStorage` (`src/demo/storage.ts`), so the web's
 * `localStorage` adapter and a test's `memoryDemoStorage()` fit as they are, and a preferences-backed adapter
 * (get / set over `putPreferences`) is what the backend build passes. Declared here rather than imported so the
 * tree module depends on nothing in `src/demo`.
 */
export interface ExpansionStorage {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

export interface ExpansionScope {
  /** `Session.user.id`; `null` / absent before sign-in exists (MVP) → the one local user. */
  userId?: string | null;
  accountId: string;
}

export const EXPANSION_STORAGE_PREFIX = 'sl.tree.expansion';

/** `sl.tree.expansion.<userId | local>.<accountId>` — per user and account, one record. */
export function expansionStorageKey(scope: ExpansionScope): string {
  return `${EXPANSION_STORAGE_PREFIX}.${scope.userId ?? 'local'}.${scope.accountId}`;
}

export interface ExpansionStore {
  readonly key: string;
  /** The remembered state, or empty when nothing is stored, the value is corrupt, or storage is unavailable. */
  load(): Promise<ExpansionState>;
  /** Remember `state`. A refused write (private browsing) is swallowed: the tree just forgets on reload. */
  save(state: ExpansionState): Promise<void>;
  /** Forget — "Reset demo" and "Start fresh" call this (REQ-910 AC3). */
  clear(): Promise<void>;
}

export function createExpansionStore(
  storage: ExpansionStorage,
  scope: ExpansionScope,
): ExpansionStore {
  const key = expansionStorageKey(scope);
  return {
    key,
    async load() {
      try {
        return parseExpansion(await storage.get(key));
      } catch {
        return EMPTY_EXPANSION;
      }
    },
    async save(state) {
      try {
        if (state.expanded.size === 0 && heldClosed(state).size === 0) await storage.remove(key);
        else await storage.set(key, serializeExpansion(state));
      } catch {
        // storage refused (quota, private browsing): nothing to do — the state lives on in memory
      }
    },
    async clear() {
      try {
        await storage.remove(key);
      } catch {
        // as above
      }
    },
  };
}
